<template>
  <ValidationObserver ref="observer" slim v-slot="{ invalid, handleSubmit }">
    <v-form @submit.prevent="handleSubmit(verifyCode)">
      <v-card :loading="loading" :class="{ 'no-shadow': isMFaView }">
        <v-card-title class="px-0">
          <v-toolbar v-if="!isMFaView" class="px-0" flat dense>
            <v-toolbar-title class="primary--text">{{ $t("headers.appAuthentication") }}</v-toolbar-title>
            <v-spacer />
            <v-btn id="dismiss" icon @click="dismiss">
              <v-icon color="primary">$close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="black--text pb-0">
          <p v-if="isMFaView" class="font-weight-bold mb-4 text-h5">{{ $t("headers.appAuthentication") }}</p>
          <div v-if="!mfaAuthWithMethods">
            <p>{{ $t("messages.mfaAppDesc") }}</p>
            <p class="mb-0">{{ $t("captions.microsoft") }}</p>
            <div class="d-flex mb-2">
              <a
                class="me-2"
                href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  class="store-badge"
                  src="../../../public/assets/img/Apple_App_Store_Badge.svg"
                  alt="apple-store-badge"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en_IN&pli=1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  class="store-badge"
                  src="../../../public/assets/img/Google_Play_Store_Badge.svg"
                  alt="google-store-badge"
                />
              </a>
            </div>
            <p class="mb-0">{{ $t("captions.google") }}</p>
            <div class="d-flex mb-2">
              <a
                class="me-2"
                href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  class="store-badge"
                  src="../../../public/assets/img/Apple_App_Store_Badge.svg"
                  alt="apple-store-badge"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_IN"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  class="store-badge"
                  src="../../../public/assets/img/Google_Play_Store_Badge.svg"
                  alt="google-store-badge"
                />
              </a>
            </div>
            <p class="font-weight-bold">{{ $t("messages.scanQR") }}</p>
            <div class="text-center QR-container">
              <v-progress-circular v-if="qrLoading" class="my-2" indeterminate size="54"></v-progress-circular>

              <!-- QR Code -->
              <img v-show="!qrLoading" :src="qrCode" alt="QR Code" height="200" width="200" @load="qrLoading = false" />
            </div>
          </div>
          <p v-else>
            {{ $t("messages.authExistingAppDesc") }}
          </p>
          <VTextFieldWithValidation
            class="pt-0"
            v-model="appCode"
            rules="required"
            :numberOnly="true"
            label="enterCode"
            placeholder="enterCode"
            maxlength="6"
            inputmode="numeric"
            autocomplete="off"
            :showCustomRequiredError="true"
          />
          <div class="d-flex justify-center">
            <error-label
              class="mb-2"
              v-model="showApiError"
              :message="errorMessage"
              ref="errorLabel"
            />
          </div>
        </v-card-text>
        <v-card-actions class="d-flex flex-column justify-center align-center pt-0 pb-8">
          <v-btn type="submit" color="primary" :disabled="invalid || invalidCodeLength">{{ $t("captions.verify") }}</v-btn>
          <v-btn v-if="mfaAuthWithMethods" class="my-6" color="primary" @click="goToAuthenticationWindow">
            {{ $t("captions.tryAnotherMethod") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </ValidationObserver>
</template>

<script lang="ts">
import Vue from "vue";

import { ValidationObserver } from "vee-validate";
import VTextFieldWithValidation from "../form/VTextFieldWithValidation.vue";
import ErrorLabel from "@/components/objects/ErrorLabel.vue";
import { parseApiError } from "@/plugins/helper";

import { AuthService, mfaTypeEnum } from "@/service";


import { RootStoreInterface } from "@/store/types";
import { createHelpers } from "vuex-typescript-interface";

const {  mapMutations } = createHelpers<RootStoreInterface>();

const setters = mapMutations(["setLoginSecurityConfirmDlg", "setMfaUsedHit"]);

export default Vue.extend({
  components: {
    ValidationObserver,
    VTextFieldWithValidation,
    ErrorLabel,
  },
  data: () => ({
    loading: false,
    appCode: "",
    qrCode: "",
    showApiError: false,
    errorMessage: "",
    qrLoading: false,
  }),
  props: {
    isParentVisible: {
      type: Boolean,
    },
    hasMfaMethods: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isMFaView(): boolean {
      return this.$route.name === "mfa" || this.$route.name === "mfaComponent";
    },
    mfaAuthWithMethods(): boolean {
      return this.hasMfaMethods && this.isMFaView;
    },
    invalidCodeLength(): boolean  {
      return this.appCode.length != 6;
    }
  },
  methods: {
    ...setters,
    showError(msg: string) {
      this.errorMessage = msg;
      this.showApiError = true;
    },
    dismiss() {
      this.$emit("dismiss");
    },
    async load() {
      try {
        this.qrLoading = true;
        const response = await AuthService.getMfaAuthAppInit();
        this.qrCode = response?.qrCodeDataUri as string;
      } catch (error: any) {
        console.error("Error loading QR code:", error);
        this.showError(parseApiError(error as any));
      } finally {
        this.qrLoading = false;
      }
    },
    async verifyCode() {
      if (this.invalidCodeLength) return;

      try {
        this.loading = true;
        const payload = {
          authCode: this.appCode,
          mfaType: mfaTypeEnum.AUTH_APP,
        };

        const res = await AuthService.getMfaVerify(payload);
        const result = (res.result || "").toString().trim().toLowerCase();
        if (
          result === "success" ||
          result === "tenantselectionrequired" ||
          result.startsWith("theme:")
        ) {
          this.$emit("mfa-verified", res.result);
          return;
        }
        this.showError("Invalid code entered. Please try again.");
      } catch (e: any) {
        const maxLimitReached = e.status == 401;
        const customError = (!this.isMFaView || maxLimitReached) ? parseApiError(e as any) : "Invalid code entered. Please try again.";
        this.showError(customError);
      } finally {
      this.loading = false;
      }
    },
    resetInitValues() {
      this.appCode = "";
      this.qrCode = "";
      this.showApiError = false;
      this.errorMessage = "";
      this.qrLoading = false;

      this.$refs.observer?.reset();
    },
    goToAuthenticationWindow() {
      this.setMfaUsedHit(false);

      this.$eventBus.$emit("hideAppBarBtn");
    },
  },
  watch: {
    isParentVisible(newVal: boolean) {
      if (newVal) {
        this.resetInitValues();
        if (!this.mfaAuthWithMethods) this.load();
      }
    },
  },
  mounted() {
    // reduce API call on already existing MFA - MFA View
    this.setLoginSecurityConfirmDlg(true);
    if (!this.mfaAuthWithMethods) this.load();
  },
});
</script>
<style lang="scss" scoped>
.QR-container {
  img {
    object-fit: contain;
  }
}

.store-badge {
  width: 100px;
  height: 30px;
  object-fit: contain;
}

.v-card.no-shadow {
  box-shadow: none;
}
</style>